import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Grid, Stack } from "@mui/material";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { SelectableBox } from "@src/appV2/redesign/components/SelectableBox";
import { type ReactNode } from "react";

import { usePersonaIdSubtypesFlag } from "./usePersonalIdSubtypes";

interface PersonalIdSubtypeSelectorProps {
  selectedSubtype: string | undefined;
  onSubtypeChange: (subtype: string) => void;
  isLoading: boolean;
  onContinue: () => void | Promise<void>;
  title?: string;
  description?: ReactNode;
}

export function PersonalIdSubtypeSelector(props: PersonalIdSubtypeSelectorProps) {
  const {
    selectedSubtype,
    onSubtypeChange,
    isLoading,
    onContinue,
    title = "Please select ID type",
    description,
  } = props;

  const { stripe: stripeDocumentSubtypes } = usePersonaIdSubtypesFlag();

  return (
    <Stack direction="column" spacing={7}>
      <Text
        semibold
        variant="h4"
        sx={{ color: (theme) => theme.palette.text.secondary, alignSelf: "center" }}
      >
        {title}
      </Text>

      {isDefined(description) && description}

      {/* Box wrapper clips Grid's negative margin to prevent layout shift */}
      <Box sx={{ overflow: "hidden" }}>
        <Grid container spacing={4}>
          {stripeDocumentSubtypes.map((subType) => (
            <Grid key={subType} item xs={6}>
              <SelectableBox
                variant="secondary"
                isSelected={selectedSubtype === subType}
                sx={{
                  width: "100%",
                }}
                onToggleSelect={() => {
                  onSubtypeChange(subType);
                }}
              >
                {subType}
              </SelectableBox>
            </Grid>
          ))}
        </Grid>
      </Box>

      <LoadingButton
        disabled={!isDefined(selectedSubtype)}
        isLoading={isLoading}
        onClick={async () => {
          await onContinue();
        }}
      >
        Continue
      </LoadingButton>
    </Stack>
  );
}
